import React, { useState, useEffect, useCallback } from "react";
import { useDispatch } from "react-redux";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Typography,
  Box,
  CircularProgress,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import { colors } from "../../constants/colors";
import { notification } from "../../redux/notification/notificationSlice";
import { changeStatus } from "../../redux/portfolio/changeStatus/statusSlice";
import { withdrawRequest } from "../../redux/holdings/holdingSlice";
import toast from "react-hot-toast";

interface OrderItem {
  id: number;
  orderId?: number;
  userId: number;
  investorName?: string;
  investorEmail?: string;
  investorMobile?: string;
  isRead: number;
  notifyType: number;
  notifyTitle: string;
  notifyMessage: string;
  requestStatus?: number;
  createdAt: string;
  investmentData?: any;
  amount?: number;
  holdingId?: number;
  investmentType?: number;
  transactionType?: string;
  assetType?: string;
}

const ASSET_CLASSES = [
  { label: "All Assets", value: "ALL" },
  { label: "Unlisted Equity", value: "UNLISTED" },
  { label: "Public Stocks", value: "STOCK" },
  { label: "AIF", value: "AIF" },
  { label: "PMS", value: "PMS" },
  { label: "Mutual Funds", value: "MF" },
  { label: "Corporate FD", value: "FD" },
  { label: "Bonds", value: "BOND" },
  { label: "SIF", value: "SIF" },
  { label: "Intl Equity", value: "INTL_EQUITY" },
  { label: "Insurance", value: "INSURANCE" },
  { label: "REIT", value: "REIT" },
  { label: "Other", value: "OTHER" },
];

const getAssetClassFromNotifyType = (item: OrderItem): string => {
  if (item.assetType && item.assetType !== "OTHER") return item.assetType;
  const notifyType = item.notifyType;
  const titleMsg = `${item.notifyTitle || ""} ${item.notifyMessage || ""}`.toLowerCase();
  const investmentType = item.investmentType;
  if (notifyType === 5 || (notifyType === 8 && titleMsg.includes("aif"))) return "AIF";
  if (notifyType === 6 || (notifyType === 8 && titleMsg.includes("pms"))) return "PMS";
  if (notifyType === 7 || (notifyType === 8 && titleMsg.includes("bond"))) return "BOND";
  if (notifyType === 4 || titleMsg.includes("mutual fund")) return "MF";
  if (notifyType === 2 || notifyType === 10 || titleMsg.includes("fixed deposit") || titleMsg.includes("fd ")) return "FD";
  if (notifyType === 13 || notifyType === 12 || notifyType === 9 || titleMsg.includes("stock") || titleMsg.includes("equity")) {
    if (investmentType === 8 || titleMsg.includes("private") || titleMsg.includes("unlisted")) {
      return "UNLISTED";
    }
    if (titleMsg.includes("stock") || notifyType === 13 || notifyType === 12) return "STOCK";
    if (notifyType === 9) {
      if (titleMsg.includes("aif")) return "AIF";
      if (titleMsg.includes("pms")) return "PMS";
      if (titleMsg.includes("bond")) return "BOND";
    }
  }
  if (notifyType === 14 || notifyType === 15) return "SIF";
  if (notifyType === 16 || notifyType === 17) return "INTL_EQUITY";
  if (notifyType === 18 || notifyType === 19) return "INSURANCE";
  if (notifyType === 22 || notifyType === 23) return "REIT";
  return "OTHER";
};

const getTransactionType = (input: OrderItem | number): string => {
  const item = typeof input === "number" ? ({ notifyType: input } as OrderItem) : input;
  if (item.transactionType && item.transactionType !== "OTHER") return item.transactionType;
  const notifyType = item.notifyType;
  if ([2, 4, 5, 6, 7, 13, 14, 16, 18, 20, 22].includes(notifyType)) return "BUY";
  if ([8, 10, 12, 15, 17, 19, 21, 23].includes(notifyType)) return "SELL";
  if (notifyType === 9) return "SHOW_INTEREST";
  if (notifyType === 3) return "PROFILE_UPDATE";
  if (notifyType === 11) return "CAS_UPLOAD";
  return "OTHER";
};

const parseAssetName = (item: OrderItem): string => {
  const msg = item.notifyMessage || "";
  if (item.notifyType === 9) {
    if (msg.includes("stock name:- ")) return msg.split("stock name:- ")[1]?.trim();
    if (msg.includes("in stock:- ")) return msg.split("in stock:- ")[1]?.trim();
    if (msg.includes("Scheme:- ")) return msg.split("Scheme:- ")[1]?.trim();
  }
  if (item.investmentData) {
    try {
      const data = typeof item.investmentData === "string" ? JSON.parse(item.investmentData) : item.investmentData;
      return (
        data?.assetName ||
        data?.schemeName ||
        data?.stockName ||
        data?.fdName ||
        data?.bondName ||
        item.notifyTitle
      );
    } catch (e) {
      if (typeof item.investmentData === "string" && !item.investmentData.startsWith("{")) {
        return item.investmentData;
      }
    }
  }
  if (msg.includes("asset:- ")) return msg.split("asset:- ")[1]?.split("has been")[0]?.trim();
  if (msg.includes("Asset:- ")) return msg.split("Asset:- ")[1]?.split("has been")[0]?.trim();
  if (msg.includes("holding:- ")) return msg.split("holding:- ")[1]?.split("has been")[0]?.trim();
  if (msg.includes("Holding:- ")) return msg.split("Holding:- ")[1]?.split("has been")[0]?.trim();
  if (msg.includes("Scheme:- ")) return msg.split("Scheme:- ")[1]?.split("has been")[0]?.trim();
  if (msg.includes("scheme:- ")) return msg.split("scheme:- ")[1]?.split("has been")[0]?.trim();
  if (msg.includes("stock name:- ")) return msg.split("stock name:- ")[1]?.trim();
  if (msg.includes("stock:- ")) return msg.split("stock:- ")[1]?.trim();
  if (msg.includes("schemeName:- ")) return msg.split("schemeName:- ")[1]?.trim();
  if (msg.includes("from PMS holding ")) return msg.split("from PMS holding ")[1]?.split("has been")[0]?.trim();
  if (msg.includes("from AIF holding ")) return msg.split("from AIF holding ")[1]?.split("has been")[0]?.trim();
  return item.notifyTitle || "Investment Request";
};

const parseAmountOrQuantity = (item: OrderItem): string => {
  if (item.amount && Number(item.amount) > 0) return `₹${Number(item.amount).toLocaleString("en-IN")}`;
  if (item.investmentData) {
    try {
      const data = typeof item.investmentData === "string" ? JSON.parse(item.investmentData) : item.investmentData;
      if (data?.investmentAmount || data?.amount) {
        return `₹${Number(data?.investmentAmount || data?.amount).toLocaleString("en-IN")}`;
      }
      if (data?.quantity || data?.units) {
        return `${data?.quantity || data?.units} Units`;
      }
    } catch (e) {}
  }
  return "-";
};

const AllOrders: React.FC = () => {
  const dispatch = useDispatch<any>();
  const [orders, setOrders] = useState<OrderItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedAsset, setSelectedAsset] = useState<string>("ALL");
  const [selectedTxType, setSelectedTxType] = useState<string>("ALL");
  const [selectedStatus, setSelectedStatus] = useState<string>("ALL");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [pageIndex] = useState<number>(1);
  const pageSize = 50;

  const [actionLoading, setActionLoading] = useState<boolean>(false);

  const fetchAllOrders = useCallback(async () => {
    setLoading(true);
    try {
      const response = await dispatch(notification({ notifyType: 0, pageIndex, pageSize }));
      if (response?.payload?.data?.data) {
        setOrders(response.payload.data.data);
      } else if (Array.isArray(response?.payload?.data)) {
        setOrders(response.payload.data);
      } else {
        setOrders([]);
      }
    } catch (error) {
      console.error("Error fetching all orders:", error);
      toast.error("Failed to load orders");
    } finally {
      setLoading(false);
    }
  }, [dispatch, pageIndex]);

  useEffect(() => {
    fetchAllOrders();
    const interval = setInterval(fetchAllOrders, 180000);
    return () => clearInterval(interval);
  }, [fetchAllOrders]);

  const handleAcceptOrder = async (item: OrderItem) => {
    const targetId = item.id;
    const txType = getTransactionType(item);
    setActionLoading(true);
    try {
      if (txType === "BUY") {
        await dispatch(changeStatus({ id: targetId, status: 1 }));
      } else {
        await dispatch(withdrawRequest({ id: targetId, status: 1, amount: 0, currentPrice: 0 }));
      }
      toast.success("Order accepted successfully");
      fetchAllOrders();
    } catch (error) {
      console.error("Error accepting order:", error);
      toast.error("Failed to accept order");
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectOrder = async (item: OrderItem) => {
    const targetId = item.id;
    const txType = getTransactionType(item);
    setActionLoading(true);
    try {
      if (txType === "BUY") {
        await dispatch(changeStatus({ id: targetId, status: 2 }));
      } else {
        await dispatch(withdrawRequest({ id: targetId, status: 2, amount: 0, currentPrice: 0 }));
      }
      toast.success("Order rejected successfully");
      fetchAllOrders();
    } catch (error) {
      console.error("Error rejecting order:", error);
      toast.error("Failed to reject order");
    } finally {
      setActionLoading(false);
    }
  };

  const filteredOrders = orders.filter((item) => {
    const assetClass = getAssetClassFromNotifyType(item);
    const txType = getTransactionType(item);

    // Hide email/CAS auto-fetch alerts — not Buy/Sell/Interest orders
    if (item.notifyType === 11 || txType === "CAS_UPLOAD") return false;

    if (selectedAsset !== "ALL" && assetClass !== selectedAsset) return false;
    if (selectedTxType !== "ALL" && txType !== selectedTxType) return false;

    const statusVal = item.requestStatus ?? 0;
    if (selectedStatus === "PENDING" && statusVal !== 0) return false;
    if (selectedStatus === "APPROVED" && statusVal !== 1) return false;
    if (selectedStatus === "REJECTED" && statusVal !== 2) return false;

    if (searchQuery.trim() !== "") {
      const q = searchQuery.toLowerCase();
      const nameMatch = item.investorName?.toLowerCase().includes(q) || false;
      const assetMatch = parseAssetName(item).toLowerCase().includes(q) || false;
      const titleMatch = item.notifyTitle.toLowerCase().includes(q);
      return nameMatch || assetMatch || titleMatch;
    }
    return true;
  });

  const getStatusChip = (statusVal?: number) => {
    if (statusVal === 1) return <Chip label="Approved" color="success" size="small" variant="outlined" />;
    if (statusVal === 2) return <Chip label="Rejected" color="error" size="small" variant="outlined" />;
    return <Chip label="Pending" color="warning" size="small" variant="outlined" />;
  };

  return (
    <Box className="w-full min-h-screen bg-gray-50 py-6 px-4 sm:px-6">
      <Box className="max-w-[98%] mx-auto">
        <Box className="flex flex-col lg:flex-row lg:items-center justify-between mb-6 gap-4">
          <div>
            <Typography variant="h5" className="font-bold text-[#255288]">
              All Orders & Requests
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Showing all Buy and Sell requests across asset classes. Use filters below to narrow down.
            </Typography>
          </div>

          <Box className="flex flex-wrap gap-3 items-center">
            <TextField
              size="small"
              placeholder="Search Investor or Scrip..."
              value={searchQuery}
              onChange={(e: any) => setSearchQuery(e.target.value)}
              className="bg-white rounded-md w-60 sm:w-64"
            />
            <FormControl size="small" className="bg-white w-44">
              <InputLabel>Asset Class</InputLabel>
              <Select
                value={selectedAsset}
                label="Asset Class"
                onChange={(e) => setSelectedAsset(e.target.value)}
              >
                {ASSET_CLASSES.map((ast) => (
                  <MenuItem key={ast.value} value={ast.value}>
                    {ast.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" className="bg-white w-40">
              <InputLabel>Transaction</InputLabel>
              <Select
                value={selectedTxType}
                label="Transaction"
                onChange={(e) => setSelectedTxType(e.target.value)}
              >
                <MenuItem value="ALL">All Types</MenuItem>
                <MenuItem value="BUY">Buy / Invest</MenuItem>
                <MenuItem value="SELL">Sell / Redeem</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" className="bg-white w-36">
              <InputLabel>Status</InputLabel>
              <Select
                value={selectedStatus}
                label="Status"
                onChange={(e) => setSelectedStatus(e.target.value)}
              >
                <MenuItem value="ALL">All Status</MenuItem>
                <MenuItem value="PENDING">Pending</MenuItem>
                <MenuItem value="APPROVED">Approved</MenuItem>
                <MenuItem value="REJECTED">Rejected</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>

        {/* Orders Table */}
        <TableContainer component={Paper} className="shadow-sm rounded-lg overflow-hidden">
          <Table>
            <TableHead sx={{ backgroundColor: "#f8fafc" }}>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold", color: "#334155" }}>Date</TableCell>
                <TableCell sx={{ fontWeight: "bold", color: "#334155" }}>Investor Name</TableCell>
                <TableCell sx={{ fontWeight: "bold", color: "#334155" }}>Asset Class</TableCell>
                <TableCell sx={{ fontWeight: "bold", color: "#334155" }}>Type</TableCell>
                <TableCell sx={{ fontWeight: "bold", color: "#334155" }}>Scrip / Scheme Name</TableCell>
                <TableCell sx={{ fontWeight: "bold", color: "#334155" }}>Amount / Quantity</TableCell>
                <TableCell sx={{ fontWeight: "bold", color: "#334155" }}>Status</TableCell>
                <TableCell align="center" sx={{ fontWeight: "bold", color: "#334155" }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                    <CircularProgress size={36} sx={{ color: colors.primary }} />
                  </TableCell>
                </TableRow>
              ) : filteredOrders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                    <Typography variant="body1" color="textSecondary">
                      No matching orders or requests found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredOrders.map((item) => {
                  const assetClass = getAssetClassFromNotifyType(item);
                  const txType = getTransactionType(item);
                  const statusVal = item.requestStatus ?? 0;

                  return (
                    <TableRow key={item.id} className="hover:bg-gray-50 transition-colors">
                      <TableCell className="text-sm text-gray-600">
                        {item.createdAt ? new Date(item.createdAt).toLocaleDateString("en-IN", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        }) : "-"}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" className="font-semibold text-gray-800">
                          {item.investorName || `User #${item.userId}`}
                        </Typography>
                        {item.investorMobile && (
                          <Typography variant="caption" className="text-gray-500 block">
                            {item.investorMobile}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={assetClass.replace("_", " ")}
                          size="small"
                          sx={{
                            fontWeight: 600,
                            backgroundColor: "#e2e8f0",
                            color: "#334155",
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={txType.replace("_", " ")}
                          size="small"
                          variant="outlined"
                          sx={{
                            fontWeight: 600,
                            borderColor: "#cbd5e1",
                            color: "#475569",
                            backgroundColor: "#f8fafc",
                          }}
                        />
                      </TableCell>
                      <TableCell className="font-medium text-gray-900 max-w-xs truncate">
                        {parseAssetName(item)}
                      </TableCell>
                      <TableCell className="font-semibold text-gray-800">
                        {parseAmountOrQuantity(item)}
                      </TableCell>
                      <TableCell>{getStatusChip(statusVal)}</TableCell>
                      <TableCell align="center">
                        {txType === "SHOW_INTEREST" ? (
                          <Chip label="Interest Shown" size="small" color="secondary" variant="outlined" />
                        ) : statusVal === 0 ? (
                          <div className="flex justify-center gap-2">
                            <Button
                              variant="contained"
                              size="small"
                              disabled={actionLoading}
                              sx={{
                                backgroundColor: "#10b981",
                                "&:hover": { backgroundColor: "#059669" },
                                textTransform: "none",
                                borderRadius: "6px",
                                px: 2,
                              }}
                              onClick={() => handleAcceptOrder(item)}
                            >
                              Accept
                            </Button>
                            <Button
                              variant="outlined"
                              size="small"
                              disabled={actionLoading}
                              sx={{
                                color: "#ef4444",
                                borderColor: "#ef4444",
                                "&:hover": { backgroundColor: "#fef2f2", borderColor: "#dc2626" },
                                textTransform: "none",
                                borderRadius: "6px",
                                px: 2,
                              }}
                              onClick={() => handleRejectOrder(item)}
                            >
                              Reject
                            </Button>
                          </div>
                        ) : (
                          <Typography variant="caption" color="textSecondary">
                            Processed
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};

export default AllOrders;
